import { createContext, useContext } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chamarEstoque } from '@/hooks/useStock';
import { formatarQuantidade } from '@/components/stock/stockFormat';
import { toast } from '@/hooks/use-toast';
import { mensagemDoErro } from '@/components/stock/stockErrorMessages';

/**
 * Demandas de compra e reservas (Bloco B, 0085).
 *
 * ⚠️ As chaves ficam FORA de `invalidateProductionQueries`. Um evento de
 * `production_steps` não pode refetchar a fila do comprador — é o mecanismo
 * exato do storm de 18/mai citado no CLAUDE.md §7.5.
 */

export type IntentMaterial = 'USAR_ESTOQUE' | 'COMPRAR' | 'MISTO';

export interface ItemDemanda {
  id: string;
  material_id: string | null;
  material_nome: string | null;
  unidade: string | null;
  /** Categoria, tecido e cor do material ligado (§13.93: casar a ribana com a malha). Ausentes em servidor antigo. */
  material_kind?: string | null;
  material_tecido?: string | null;
  material_cor?: string | null;
  descricao: string | null;
  intent: IntentMaterial;
  qty_planejada: number | null;
  qty_recebida: number;
  saldo: number;
  reservado: number;
  reservado_neste_pedido: number;
  /**
   * A parte de `reservado_neste_pedido` que veio da CHEGADA de uma compra (reserva automática do recebimento, 0124,
   * §13.79) — malha comprada e já separada, não reserva feita à mão do estoque. Ausente em servidor antigo.
   */
  reservado_de_compra_neste_pedido?: number;
  /** Σ do que já SAIU do galpão para o pedido deste material (reservas ≠ CANCELADA). */
  consumido_neste_pedido: number;
  /**
   * A compra POR ITEM (0120, §13.73): cada item guarda de quem, como chega e quando — o mesmo
   * pedido pode ter a malha de um fornecedor e a gola de outro. `comprado_em` nulo = ainda por comprar.
   */
  fornecedor_texto?: string | null;
  entrega_modo?: EntregaModo | null;
  previsao_chegada?: string | null;
  comprado_em?: string | null;
}

export interface Demanda {
  id: string;
  origin_type: 'PEDIDO' | 'AVULSA' | 'RETRABALHO';
  status: string;
  order_id: string | null;
  /** Demanda de retrabalho: a ocorrência dela (o card do quadro casa por aqui). */
  occurrence_id?: string | null;
  observacao: string | null;
  nota_automatica: string | null;
  /** NF e observações do card de COMPRAS do quadro (pedidos de antes da virada de 03/10/2026). */
  observacoes_card?: string | null;
  /** Fornecedor em texto livre da compra avulsa (0100). */
  fornecedor_texto: string | null;
  /** Documento(s) do GestãoClick dos recebimentos casados com esta demanda. */
  documentos: string | null;
  criado_por: string | null;
  created_at: string;
  numero_pedido: string | null;
  cliente: string | null;
  data_prometida: string | null;
  /** O layout do pedido (nulo na avulsa e no pedido sem arte). */
  link_layout?: string | null;
  itens: ItemDemanda[];
  /** FORNECEDOR_ENTREGA | BUSCAR — informado no "Comprei" (0113, §13.73). */
  entrega_modo?: 'FORNECEDOR_ENTREGA' | 'BUSCAR' | null;
  /** Data prevista de chegada, 'AAAA-MM-DD' (0113). */
  previsao_chegada?: string | null;
  comprado_em?: string | null;
  updated_at?: string;
  /** Situação do pedido (`PENDENTE_PROGRAMACAO`, `EM_PRODUCAO`…); nulo na compra avulsa. */
  order_status?: string | null;
  /** A compra foi antecipada pelo "Antecipar compra" do card em Aguardando programação. */
  antecipada?: boolean;
  /**
   * É SÓ a falta do corte (a compra pequena do "Faltou 7 kg"), decidido pelo SERVIDOR — a mesma regra que cria ou não
   * o card de COMPRAS. O marcador no texto da observação sozinho errava a demanda mista (a falta + a linha da
   * programação na mesma demanda): ela tem card e não é "só a falta".
   */
  falta_do_corte?: boolean;
}

export type EntregaModo = 'FORNECEDOR_ENTREGA' | 'BUSCAR';

/** O "Comprei" de UMA demanda inteira (a compra avulsa nasce assim). */
export interface CompreiInput {
  id: string;
  fornecedor_texto: string;
  entrega_modo: EntregaModo;
  /** 'AAAA-MM-DD'; vazio = ainda não sabe. */
  previsao_chegada?: string;
}

/** O "Comprei" por ITEM: um card pode ter fornecedores diferentes, e "Comprar vários" junta pedidos. */
export interface CompreiItensInput {
  item_ids: string[];
  fornecedor_texto: string;
  entrega_modo: EntregaModo;
  previsao_chegada?: string;
}

/** Fornecedor favorito da organização — o botão do "Comprei" (0120). */
export interface FornecedorFavorito {
  id: string;
  nome: string;
  gc_id: string | null;
}

/** O que o cortador precisa saber antes de ir ao galpão. */
export interface MaterialDoPedido {
  /** Necessário para a ação "faltou" por linha. */
  material_id?: string;
  /** Reserva viva da linha (para o "Liberar reserva"); null = não há. */
  reserva_id?: string | null;
  material: string;
  unidade: string;
  qty: number;
  qty_consumida?: number;
  /** Retirado de fato para o pedido, pelo ledger (rolo inteiro). */
  qty_retirada?: number;
  rolos?: string[];
  /** Como o material se rastreia: só o INDIVIDUAL (rolo) é bipado no corte (§13.57). */
  tracking?: 'INDIVIDUAL' | 'PACOTE' | 'AGREGADO';
  qty_planejada?: number | null;
  qty_recebida?: number | null;
  /** Só com a compra a caminho e a data prevista informada (§13.73). */
  previsao_chegada?: string | null;
  /** > 0 = a data prevista já passou (a mercadoria está atrasada). */
  dias_atraso?: number | null;
  situacao: 'RESERVADO' | 'EM_COMPRA' | 'COMPRADO_AGUARDANDO' | 'RECEBIDO' | 'A_COMPRAR' | 'RETIRADO';
}

export const demandKeys = {
  all: ['stock', 'demands'] as const,
  lista: (status?: string, orderIds?: string[]) =>
    ['stock', 'demands', status ?? 'abertas', orderIds?.join(',') ?? ''] as const,
  materiaisDosPedidos: (ids: string[]) => ['stock', 'order-materials', ids.join(',')] as const,
};

/**
 * `orderIds`: a pergunta do quadro ("quais destes pedidos têm demanda viva?")
 * é por pedido, sem LIMIT — a fila por prazo (sem `orderIds`) é paginada em
 * 200 e, com o histórico crescendo, a demanda viva de um pedido caía fora da
 * página e o card de COMPRAS reaparecia no quadro antigo.
 */
export function useDemandas(status?: string, ligado = true, orderIds?: string[], recentes?: number) {
  const ids = orderIds ? [...new Set(orderIds.filter(Boolean))].sort() : undefined;
  return useQuery({
    queryKey: [...demandKeys.lista(status, ids), recentes ?? 0],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      // Só o que mudou nos últimos N dias, o mais recente primeiro ("Chegou").
      if (recentes) qs.set('recentes', String(recentes));
      if (ids?.length) qs.set('order_ids', ids.join(','));
      const q = qs.toString();
      return chamarEstoque<Demanda[]>(`/api/stock/demands${q ? `?${q}` : ''}`);
    },
    enabled: ligado && (ids === undefined || ids.length > 0),
    staleTime: 60000,
  });
}

/** Um resultado da busca de Compras (demanda, card do quadro ou avulsa do quadro). */
export interface ItemDoResultado {
  material: string | null;
  unidade: string | null;
  comprado: number | null;
  recebido: number;
  fornecedor: string | null;
  /** Guardado para o pedido (reserva ativa + já cortado) e a parte que já saiu para o corte. */
  reservado?: number | null;
  cortado?: number | null;
  intent?: string;
}

export interface ResultadoBuscaCompra {
  tipo: 'DEMANDA' | 'CARD' | 'AVULSA_QUADRO';
  id: string;
  status: string;
  order_id: string | null;
  numero_pedido: string | null;
  cliente: string | null;
  fornecedor: string | null;
  descricao: string | null;
  nota_fiscal: string | null;
  entrega_modo: string | null;
  previsao_chegada: string | null;
  observacao: string | null;
  quando: string | null;
  /** Só na demanda: o que foi comprado e o que já chegou, item a item. */
  itens?: ItemDoResultado[] | null;
  comprado_em?: string | null;
}

/**
 * Busca de Compras no servidor — inclui o que JÁ FOI CONCLUÍDO (pedido, NF,
 * fornecedor, material, cliente, texto do card). Só com 2+ letras.
 */
export function useBuscaCompras(termo: string) {
  const q = termo.trim();
  return useQuery({
    queryKey: ['stock', 'compras-busca', q],
    queryFn: () => chamarEstoque<ResultadoBuscaCompra[]>(`/api/stock/compras/busca?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

/**
 * Quantas compras estão atrasadas — o contador do painel do gerente.
 *
 * ⚠️ Chave FORA do prefixo `['stock']`, como a contagem de pendências: o painel
 * existe em toda aba de gerente e não pode refazer a consulta a cada bipe do
 * galpão. Quem a invalida é o listener de `purchase_demands`. Atraso é função do
 * TEMPO, não só de evento (a data vira meia-noite sem ninguém mexer), por isso
 * também reconta de tempos em tempos.
 */
export function useComprasAtrasadas(ligado = true) {
  return useQuery({
    queryKey: ['stock-alerts', 'compras-atrasadas'],
    queryFn: () => chamarEstoque<{ atrasadas: number }>('/api/stock/demands-atrasadas'),
    enabled: ligado,
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Materiais de VÁRIOS pedidos numa chamada — o hook-mapa.
 *
 * Uma query para todos os cards visíveis, no molde do `useOrderMetadataMap`.
 * Query por card seria o N+1 do `observacoes-count`, que o CLAUDE.md manda
 * explicitamente não copiar.
 */
export function useMateriaisDosPedidos(orderIds: string[], ligado = true) {
  const ids = [...new Set(orderIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: demandKeys.materiaisDosPedidos(ids),
    // Em blocos de 100: a URL com todos os ids passava de 3 kB com o quadro
    // cheio (cada uuid são 37 caracteres); 100 ≈ 3,7 kB cabe em nginx/Cloudflare.
    queryFn: async () => {
      const blocos: string[][] = [];
      for (let i = 0; i < ids.length; i += 100) blocos.push(ids.slice(i, i + 100));
      const partes = await Promise.all(
        blocos.map((b) =>
          chamarEstoque<Record<string, MaterialDoPedido[]>>(`/api/stock/order-materials?ids=${b.join(',')}`),
        ),
      );
      return Object.assign({}, ...partes) as Record<string, MaterialDoPedido[]>;
    },
    enabled: ligado && ids.length > 0,
    // A chave muda quando um card de corte entra/sai do quadro: sem isto todos
    // os selos de material somem até a nova resposta chegar.
    placeholderData: keepPreviousData,
    staleTime: 120000,
    refetchOnWindowFocus: false,
  });
}

function avisarErro(err: Error & { code?: string }) {
  const m = mensagemDoErro(err.code);
  toast({ title: m.titulo, description: m.oQueFazer, variant: 'destructive' });
}

/**
 * Fornecedores favoritos — por ORGANIZAÇÃO, não por usuário. Relido toda vez que o "Comprei"
 * abre (`staleTime: 0`): é uma lista curta e o colega pode ter favoritado há um minuto.
 */
export const favoritosKey = ['stock', 'fornecedores-favoritos'] as const;

export function useFornecedoresFavoritos(ligado = true) {
  return useQuery({
    queryKey: favoritosKey,
    queryFn: () => chamarEstoque<FornecedorFavorito[]>('/api/stock/fornecedores/favoritos'),
    enabled: ligado,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useFavoritosMutations() {
  const qc = useQueryClient();
  const favoritar = useMutation({
    mutationFn: (f: { nome: string; gc_id?: string | null }) =>
      chamarEstoque<{ id: string | null; ja_existia: boolean }>('/api/stock/fornecedores/favoritos', {
        method: 'POST', body: { nome: f.nome, gc_id: f.gc_id ?? undefined },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: favoritosKey }),
    onError: avisarErro,
  });
  const desfavoritar = useMutation({
    mutationFn: (id: string) =>
      chamarEstoque<{ removido: boolean }>(`/api/stock/fornecedores/favoritos/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: favoritosKey }),
    onError: avisarErro,
  });
  return { favoritar, desfavoritar };
}

export function useDemandaMutations() {
  const qc = useQueryClient();
  const invalidar = () => {
    qc.invalidateQueries({ queryKey: demandKeys.all });
    qc.invalidateQueries({ queryKey: ['stock', 'order-materials'] });
    qc.invalidateQueries({ queryKey: ['stock', 'balances'] });
  };

  const avancar = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      chamarEstoque(`/api/stock/demands/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  const reservar = useMutation({
    mutationFn: ({ id, demand_item_id, qty, qty_desejada }: { id: string; demand_item_id: string; qty: number; qty_desejada?: number }) =>
      chamarEstoque<{ qty: number; disponivel_restante: number; unidade?: string | null; a_comprar?: number }>(
        `/api/stock/demands/${id}/reserve`,
        { method: 'POST', body: { demand_item_id, qty, qty_desejada } },
      ),
    onSuccess: (d) => {
      invalidar();
      // Reserva parcial: o que não deu para reservar ficou como compra — a
      // linha muda para "usar estoque + comprar" e o comprador precisa saber.
      toast({
        title: d.a_comprar ? '✅ Reservado o que havia — o resto ficou para comprar' : '✅ Material reservado',
        description: d.a_comprar
          ? `Faltam ${formatarQuantidade(d.a_comprar, d.unidade ?? '')} a comprar; a demanda continua na fila "A comprar".`
          : `Sobram ${formatarQuantidade(d.disponivel_restante, d.unidade ?? '')} disponíveis deste material.`,
      });
    },
    onError: avisarErro,
  });

  // Cancelar exige motivo (vira a nota da demanda); o servidor solta a reserva
  // e fecha a falta do corte que apontava para ela.
  const cancelar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      chamarEstoque<{ reservas_canceladas: number; faltas_resolvidas: number }>(
        `/api/stock/demands/${id}`,
        { method: 'PATCH', body: { status: 'CANCELADA', motivo } },
      ),
    onSuccess: (d) => {
      invalidar();
      qc.invalidateQueries({ queryKey: ['stock', 'pending-tasks'] });
      toast({
        title: 'Demanda cancelada',
        description: d.reservas_canceladas
          ? `${d.reservas_canceladas} reserva(s) liberada(s).`
          : 'Ela sai da fila; o motivo fica registrado nela.',
      });
    },
    onError: avisarErro,
  });

  const removerItem = useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      chamarEstoque<{ removido: string }>(`/api/stock/demands/${id}/items/${itemId}`, { method: 'DELETE' }),
    onSuccess: () => { invalidar(); toast({ title: 'Item removido da demanda' }); },
    onError: avisarErro,
  });

  const ligarMaterial = useMutation({
    mutationFn: ({ id, itemId, material_id }: { id: string; itemId: string; material_id: string }) =>
      chamarEstoque<{ material_nome: string; reservas_canceladas: number }>(
        `/api/stock/demands/${id}/items/${itemId}`,
        { method: 'PATCH', body: { material_id } },
      ),
    onSuccess: (d) => {
      invalidar();
      toast({
        title: `✅ Item ligado a ${d.material_nome}`,
        description: d.reservas_canceladas
          ? `${d.reservas_canceladas} reserva(s) do material anterior cancelada(s). Reserve de novo se precisar.`
          : 'Saldo e reserva passam a valer para este material.',
      });
    },
    onError: avisarErro,
  });

  // A quantidade a comprar, sem passar pela reserva: item COMPRAR sem saldo
  // não tinha onde ser dimensionado e ficava "a definir" para sempre.
  const definirQuantidade = useMutation({
    mutationFn: ({ id, itemId, qty_planejada }: { id: string; itemId: string; qty_planejada: number }) =>
      chamarEstoque<{ qty_planejada: number }>(
        `/api/stock/demands/${id}/items/${itemId}`,
        { method: 'PATCH', body: { qty_planejada } },
      ),
    onSuccess: () => invalidar(),
    onError: avisarErro,
  });

  // O texto livre do item da compra avulsa sem pedido (§13.89). O servidor deixa rastro na observação da demanda.
  const editarTexto = useMutation({
    mutationFn: ({ id, itemId, descricao }: { id: string; itemId: string; descricao: string }) =>
      chamarEstoque<{ descricao: string }>(
        `/api/stock/demands/${id}/items/${itemId}`,
        { method: 'PATCH', body: { descricao } },
      ),
    onSuccess: () => {
      invalidar();
      toast({ title: 'Texto do item alterado' });
    },
    onError: avisarErro,
  });

  // "Comprar em vez de reservar" e o contrário (§13.82): a intenção do item é decisão do comprador, mesmo com
  // malha no galpão. O servidor só aceita no item que ainda não moveu nada; o card de COMPRAS segue a demanda.
  // Os itens do card de uma vez (um PATCH por item, em sequência — o servidor trava a demanda), UM toast e UMA
  // invalidação; o primeiro erro interrompe e o toast de erro explica.
  const mudarIntencao = useMutation({
    // `qty_planejada` acompanha a troca no MESMO PATCH: o "Comprar tudo" da janela de itens já leva o total digitado.
    mutationFn: async ({ id, itemIds, intent, qty_planejada }: { id: string; itemIds: string[]; intent: 'COMPRAR' | 'USAR_ESTOQUE'; qty_planejada?: number }) => {
      for (const itemId of itemIds) {
        await chamarEstoque<{ intent: string }>(`/api/stock/demands/${id}/items/${itemId}`, { method: 'PATCH', body: { intent, qty_planejada } });
      }
      return { intent, quantos: itemIds.length };
    },
    onSuccess: (d) => {
      invalidar();
      qc.invalidateQueries({ queryKey: ['production-steps'] });
      const plural = d.quantos > 1;
      toast({
        title: d.intent === 'COMPRAR' ? (plural ? 'Os itens vão ser comprados' : 'O item vai ser comprado') : (plural ? 'Os itens saem do estoque' : 'O item sai do estoque'),
        description: d.intent === 'COMPRAR' ? (plural ? 'Foram para "A comprar".' : 'Ele foi para "A comprar".') : (plural ? 'Foram para "Reservar".' : 'Ele foi para "Reservar".'),
      });
    },
    onSettled: () => { invalidar(); qc.invalidateQueries({ queryKey: ['production-steps'] }); },
    onError: avisarErro,
  });

  // "Comprei" (§13.73): leva a demanda a "a caminho" e registra fornecedor,
  // como chega e a data prevista. Com a demanda já a caminho, só edita.
  const comprei = useMutation({
    mutationFn: ({ id, ...corpo }: CompreiInput) =>
      chamarEstoque<{ id: string; status: string; editou: boolean }>(
        `/api/stock/demands/${id}/comprei`,
        { method: 'POST', body: { ...corpo, previsao_chegada: corpo.previsao_chegada || undefined } },
      ),
    onSuccess: (d) => {
      invalidar();
      toast({ title: d.editou ? '✅ Informações da compra atualizadas' : '✅ Compra registrada — está a caminho' });
    },
    onError: avisarErro,
  });

  // O "Comprei" por item (0120): um card com dois fornecedores, ou vários pedidos de uma vez.
  // A demanda só vai a "a caminho" quando o ÚLTIMO item a comprar é marcado.
  const compreiItens = useMutation({
    mutationFn: ({ previsao_chegada, ...corpo }: CompreiItensInput) =>
      chamarEstoque<{ id: string; status: string; editou: boolean; itens: number; demandas: Array<{ id: string; status: string }> }>(
        '/api/stock/demands/comprei-itens',
        { method: 'POST', body: { ...corpo, previsao_chegada: previsao_chegada || undefined } },
      ),
    onSuccess: (d) => {
      invalidar();
      qc.invalidateQueries({ queryKey: ['production-steps'] });
      const aCaminho = d.demandas?.some((x) => x.status === 'AGUARDANDO_CHEGADA');
      toast({
        title: d.editou ? '✅ Informações da compra atualizadas' : aCaminho ? '✅ Compra registrada — está a caminho' : '✅ Compra registrada',
        description: d.editou || aCaminho ? undefined : 'Falta comprar o resto dos itens deste pedido.',
      });
    },
    onError: avisarErro,
  });

  const criarAvulsa = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      chamarEstoque<{ id: string }>('/api/stock/demands', { method: 'POST', body }),
    onSuccess: () => {
      invalidar();
      toast({ title: '✅ Demanda criada' });
    },
    onError: avisarErro,
  });

  // Ligar a compra avulsa (sem pedido) a um pedido (§13.89): ela vira a compra do pedido, ou entra na que ele já tem.
  const ligarPedido = useMutation({
    mutationFn: ({ id, order_id }: { id: string; order_id: string }) =>
      chamarEstoque<{ id: string; juntou: boolean; numero_pedido: string }>(
        `/api/stock/demands/${id}/ligar-pedido`, { method: 'POST', body: { order_id } },
      ),
    onSuccess: (r) => {
      invalidar();
      qc.invalidateQueries({ queryKey: ['production-steps'] });
      toast({
        title: `✅ Compra ligada ao pedido ${r.numero_pedido}`,
        description: r.juntou ? 'O pedido já tinha compra aberta: os itens entraram nela.' : undefined,
      });
    },
    // A frase do servidor diz o motivo exato (cancelado, concluído, já chegou…) — ver `usePedirComplemento`.
    onError: (err: Error & { code?: string }) => {
      const m = mensagemDoErro(err.code);
      toast({ title: 'Não deu para ligar a compra ao pedido', description: err.message || m.oQueFazer, variant: 'destructive' });
    },
  });

  return { avancar, reservar, criarAvulsa, ligarMaterial, definirQuantidade, editarTexto, mudarIntencao, cancelar, removerItem, comprei, compreiItens, ligarPedido };
}

export interface ComplementoInput {
  order_id: string;
  material_id?: string;
  descricao?: string;
  qty_planejada?: number;
  /** Opcional: "por que precisa de mais" — vai na nota da compra, mas não é exigido. */
  motivo?: string;
  /** A "Nova compra avulsa" ligada a um pedido (§13.89): a nota diz "compra avulsa", não "complemento". */
  avulsa?: boolean;
  /** O fornecedor que a compra avulsa já escolheu. */
  fornecedor_texto?: string;
  /** Avulsa já comprada: entra como item separado, nunca somada ao item do pedido ainda por comprar. */
  ja_comprado?: boolean;
}

/**
 * "Acrescentar item" (§13.73/§13.75): mais um item na compra DO PEDIDO — malha, punho, aviamento…
 * Entra na compra em aberto; com a compra a caminho o item novo nasce por comprar (o card volta a
 * "A comprar", "1 de 2 comprados"); sem compra viva abre uma demanda nova e a etapa COMPRAS volta
 * ao quadro. O que já foi comprado fica como está. Invalida a
 * fila e as etapas do pedido (o card de COMPRAS reaparece no painel).
 */
export function usePedirComplemento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ComplementoInput) =>
      chamarEstoque<{ id: string; item_id?: string; reaproveitada: boolean; item_somado?: boolean; step_reaberto: boolean; step_criado: boolean; voltou_a_comprar?: boolean }>(
        '/api/stock/demands/complemento', { method: 'POST', body: input },
      ),
    onSuccess: (r, input) => {
      qc.invalidateQueries({ queryKey: demandKeys.all });
      qc.invalidateQueries({ queryKey: ['stock', 'order-materials'] });
      // `order_steps` (sublinhado) é a chave de `useOrderSteps` — a linha da
      // etapa COMPRAS no detalhe do pedido; com hífen ninguém era invalidado.
      qc.invalidateQueries({ queryKey: ['order_steps', input.order_id] });
      qc.invalidateQueries({ queryKey: ['production-steps'] });
      toast({
        title: r.item_somado ? '✅ Quantidade somada ao item que já estava por comprar'
          : input.avulsa ? '✅ Compra criada no pedido' : '✅ Item acrescentado à compra do pedido',
        description: r.voltou_a_comprar
          ? 'O que já foi comprado continua a caminho. Este item entra em "A comprar".'
          : r.step_reaberto ? 'A etapa COMPRAS do pedido voltou para o quadro.' : undefined,
      });
    },
    // A frase do servidor diz o motivo exato (compra a caminho, pedido concluído,
    // peça lisa…). O mapa por código é genérico: TRANSICAO_INVALIDA vira "este passo
    // não vem depois do atual… atualize a tela", que NÃO é o que aconteceu — todas as
    // recusas do complemento ficavam inalcançáveis (revisão 2026-10-06, CLAUDE.md §10).
    onError: (err: Error & { code?: string }, input) => {
      const m = mensagemDoErro(err.code);
      // A avulsa com pedido: o título genérico do código ("Este passo não vem depois do atual") não diz nada aqui.
      toast({ title: input.avulsa ? 'Não deu para pôr a compra neste pedido' : m.titulo, description: err.message || m.oQueFazer, variant: 'destructive' });
    },
  });
}

export interface FaltaInput {
  material_id: string;
  order_id?: string;
  qty_faltante?: number;
  observacao?: string;
}

/**
 * Registrar falta de material — o "e agora?" do SALDO_INSUFICIENTE.
 *
 * ⚠️ Sem isto o cortador descobre a falta no galpão, o sistema responde "saldo
 * insuficiente" e acaba: ele volta para a mesa e o comprador não fica sabendo
 * de nada. A fábrica descobre quando o pedido atrasa. A rota cria a pendência
 * (o aviso endereçado — uma por pedido × material, registrar de novo atualiza)
 * e o item a comprar na demanda do pedido (na viva que já existe, ou numa
 * avulsa nova) numa transação — só uma das duas seria aviso sem ação, ou fila
 * sem origem. Com `compras` desligado fica só a pendência (`demanda_id` nulo).
 */
export function useRegistrarFalta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FaltaInput) =>
      chamarEstoque<{
        pendencia_id: string;
        /** Já havia uma falta ABERTA deste material neste pedido: foi atualizada, não duplicada. */
        pendencia_ja_existia: boolean;
        /** Nulo com a capacidade `compras` desligada — fica só a pendência. */
        demanda_id: string | null;
        /** O item entrou na demanda viva do pedido (a da programação), em vez de abrir outra. */
        demanda_reaproveitada: boolean;
        material: string;
      }>(
        '/api/stock/faltas',
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: demandKeys.all });
      qc.invalidateQueries({ queryKey: ['stock'] });
    },
  });
}


/**
 * ⚠️ O HOOK-MAPA DE VERDADE. `useMateriaisDosPedidos([step.order_id])` dentro
 * de cada card é uma query por card — a chave inclui os ids, então 20 cards de
 * CORTE são 20 requisições, refeitas a cada evento de estoque. É exatamente o
 * N+1 do `observacoes-count` que o CLAUDE.md manda não copiar, e o comentário
 * do card dizia o contrário ("o TanStack dedupe") — só deduplica ids IGUAIS.
 *
 * O quadro (`OperatorBoardView`) faz UMA chamada com os pedidos de todos os
 * cards visíveis e distribui pelo contexto; o card lê daqui e só cai na
 * própria query quando renderizado fora de um quadro.
 */
export const MateriaisDosPedidosContext = createContext<Record<string, MaterialDoPedido[]> | null>(null);

export function useMateriaisDoPedidoDoQuadro(orderId: string | null | undefined, habilitado: boolean) {
  const doQuadro = useContext(MateriaisDosPedidosContext);
  const proprio = useMateriaisDosPedidos(
    doQuadro === null && habilitado && orderId ? [orderId] : [],
    doQuadro === null && habilitado,
  );
  if (doQuadro !== null) return orderId ? doQuadro[orderId] : undefined;
  return orderId ? proprio.data?.[orderId] : undefined;
}
